import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
} from '@mui/material';
import useLocalization from 'hooks/useLocalization';
import React from 'react';
import { store, useSelector } from 'store';
import { fetchUpdateUserConfig } from 'store/slices/userConfig';
import { ChatUserModel } from 'types/chat-users/chat-user';
import { ViewCondition } from 'types/userConfig/userConfig';
import { generateUniqueId, safeParseJSON } from 'utils/helpers';
import Grid from '@mui/material/Grid2';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import CreateCustomViewConditions from './CreateCustomViewConditions';
import { useNavigate, useParams } from 'react-router';

interface CreateCustomViewProps {
  open: boolean;
  onClose: () => void;
  createOrEdit: 'create' | 'edit' | null;
}
export interface CreateCustomViewRefProps {
  setData: (id: string | number | null, name: string, conditions: any[]) => void;
  clearData: () => void;
}
export type ViewFilterOptions = {
  key: string;
  label: string | null;
  type: 'input' | 'select' | 'dateTime' | 'checkbox';
  options?: Array<{ value: any; label: string | null }>;
  multiple?: boolean;
};

const CreateCustomView = React.forwardRef<CreateCustomViewRefProps, CreateCustomViewProps>(
  ({ open, onClose, createOrEdit }, ref) => {
    //* localization
    const { currentLocale } = useLocalization();
    const localization = React.useMemo(
      () => currentLocale.settingsPopup.navigation.filters.create,
      [currentLocale],
    );

    //* Assigness
    const applicationUsers = useSelector((state) => state.applicationUsers);
    const selectableAssignees = React.useMemo(() => {
      return applicationUsers
        ?.filter((user) => {
          const userProfileConfig = safeParseJSON(user.ProfileConfig);

          const visible =
            userProfileConfig?.funnerInfo?.visibleInList !== undefined &&
            userProfileConfig?.funnerInfo?.visibleInList !== null
              ? userProfileConfig.funnerInfo.visibleInList
              : user.IsActive;

          return visible;
        })
        .filter((user) => user.FullName !== null && user.FullName !== undefined);
    }, [applicationUsers]);

    //* labels
    const chatUsersLabelsList = useSelector((state) => state.labels);

    const filterOptions: Array<ViewFilterOptions> = React.useMemo(
      () => [
        // Contact attributes
        {
          key: 'IsOpen',
          label: localization.options.lastStatus.label,
          type: 'select',
          options: [
            {
              value: true,
              label: localization.options.lastStatus.value.open,
            },
            {
              value: false,
              label: localization.options.lastStatus.value.close,
            },
          ],
        },
        {
          key: 'IsOpenUpdateTimeFrom',
          label: localization.options.lastStatusTimeAndDate.label + ' מ-',
          type: 'dateTime',
        },
        {
          key: 'IsOpenUpdateTimeTo',
          label: localization.options.lastStatusTimeAndDate.label + ' עד-',
          type: 'dateTime',
        },
        {
          key: 'NotificationContactUser',
          label: localization.options.assignee.label,
          type: 'select',
          multiple: true,
          options: selectableAssignees.map((user) => ({
            value: user.Udid,
            label: user.FullName,
          })),
        },
        {
          key: 'LableName',
          label: 'תגית',
          type: 'select',
          options:
            chatUsersLabelsList?.map((label) => ({
              value: JSON.stringify(label),
              label: label.name,
            })) || [],
        },
        // Last message attributes
        {
          key: 'LastMessageMediaType',
          label: 'סוג ההודעה האחרונה',
          type: 'select',
          options: [
            {
              value: 'template',
              label: 'תבנית',
            },
            {
              value: 'regular',
              label: 'רגילה',
            },
          ],
        },
        // {
        //   key: 'LastMessageIsImage',
        //   label: 'Whether the last message contains an image',
        //   type: 'select',
        //   options: [
        //     {
        //       value: true,
        //       label: 'True',
        //     },
        //     {
        //       value: false,
        //       label: 'False',
        //     },
        //   ],
        // },
        // {
        //   key: 'LastMessageIsVideo',
        //   label: 'Whether the last message contains a video',
        //   type: 'select',
        //   options: [
        //     {
        //       value: true,
        //       label: 'True',
        //     },
        //     {
        //       value: false,
        //       label: 'False',
        //     },
        //   ],
        // },
        // {
        //   key: 'LastMessageIsAudio',
        //   label: 'Whether the last message contains an audio file',
        //   type: 'select',
        //   options: [
        //     {
        //       value: true,
        //       label: 'True',
        //     },
        //     {
        //       value: false,
        //       label: 'False',
        //     },
        //   ],
        // },
        {
          key: 'LastMessageSource',
          label: 'מקור ההודעה האחרונה',
          type: 'select',
          options: [
            {
              value: 'user',
              label: 'משתמש',
            },
            {
              value: 'bot',
              label: 'בוט',
            },
            {
              value: 'massMessaging',
              label: 'הודעות המוניות',
            },
          ],
        },
        // Last incoming message attributes
        {
          key: 'IncomingLastMessageMediaType',
          label: 'סוג ההודעה הנכנסת האחרונה',
          type: 'select',
          options: [
            {
              value: 'template',
              label: 'תבנית',
            },
            {
              value: 'regular',
              label: 'רגילה',
            },
          ],
        },
        {
          key: 'IncomingLastMessageDateFrom',
          label: 'זמן ההודעה הנכנסת האחרונה מ-',
          type: 'dateTime',
        },
        {
          key: 'IncomingLastMessageDateTo',
          label: 'זמן ההודעה הנכנסת האחרונה עד-',
          type: 'dateTime',
        },
        // {
        //   key: 'IncomingLastMessageIsImage',
        //   label: 'Whether the last incoming message contains an image',
        //   type: 'select',
        //   options: [
        //     {
        //       value: true,
        //       label: 'True',
        //     },
        //     {
        //       value: false,
        //       label: 'False',
        //     },
        //   ],
        // },
        // {
        //   key: 'IncomingLastMessageIsVideo',
        //   label: 'Whether the last incoming message contains a video',
        //   type: 'select',
        //   options: [
        //     {
        //       value: true,
        //       label: 'True',
        //     },
        //     {
        //       value: false,
        //       label: 'False',
        //     },
        //   ],
        // },
        // {
        //   key: 'IncomingLastMessageIsAudio',
        //   label: 'Whether the last incoming message contains an audio file',
        //   type: 'select',
        //   options: [
        //     {
        //       value: true,
        //       label: 'True',
        //     },
        //     {
        //       value: false,
        //       label: 'False',
        //     },
        //   ],
        // },
        {
          key: 'IncomingLastMessageSource',
          label: 'מקור ההודעה הנכנסת האחרונה',
          type: 'select',
          options: [
            {
              value: 'user',
              label: 'משתמש',
            },
            {
              value: 'system',
              label: 'מערכת',
            },
            {
              value: 'externalNumber',
              label: 'מספר חיצוני',
            },
          ],
        },
        {
          key: 'IncomingLastMessageContactUser',
          label: 'המשתמש שהשיחה הוקצתה אליו כשההודעה הנכנסת התקבלה',
          type: 'select',
          multiple: true,
          options: selectableAssignees.map((user) => ({
            value: user.Udid,
            label: user.FullName,
          })),
        },
        // Last outcoming message attributes
        {
          key: 'OutcomingLastMessageMediaType',
          label: 'סוג ההודעה היוצאת האחרונה',
          type: 'select',
          options: [
            {
              value: 'template',
              label: 'תבנית',
            },
            {
              value: 'regular',
              label: 'רגילה',
            },
          ],
        },
        {
          key: 'OutcomingLastMessageDateFrom',
          label: 'זמן ההודעה היוצאת האחרונה מ-',
          type: 'dateTime',
        },
        {
          key: 'OutcomingLastMessageDateTo',
          label: 'זמן ההודעה היוצאת האחרונה עד-',
          type: 'dateTime',
        },
        // {
        //   key: 'OutcomingLastMessageIsImage',
        //   label: 'Whether the last outgoing message contains an image',
        //   type: 'select',
        //   options: [
        //     {
        //       value: true,
        //       label: 'True',
        //     },
        //     {
        //       value: false,
        //       label: 'False',
        //     },
        //   ],
        // },
        // {
        //   key: 'OutcomingLastMessageIsVideo',
        //   label: 'Whether the last outgoing message contains a video',
        //   type: 'select',
        //   options: [
        //     {
        //       value: true,
        //       label: 'True',
        //     },
        //     {
        //       value: false,
        //       label: 'False',
        //     },
        //   ],
        // },
        // {
        //   key: 'OutcomingLastMessageIsAudio',
        //   label: 'Whether the last outgoing message contains an audio file',
        //   type: 'select',
        //   options: [
        //     {
        //       value: true,
        //       label: 'True',
        //     },
        //     {
        //       value: false,
        //       label: 'False',
        //     },
        //   ],
        // },
        {
          key: 'OutcomingLastMessageSource',
          label: 'מקור ההודעה היוצאת האחרונה',
          type: 'select',
          options: [
            {
              value: 'user',
              label: 'משתמש',
            },
            {
              value: 'bot',
              label: 'בוט',
            },
            {
              value: 'broadcast',
              label: 'שידור',
            },
          ],
        },
        // {
        //   key: "OutcomingLastMessageSourceName",
        //   label: "The name of the user, bot, or broadcast that sent the message",
        //   type: "input",
        // },
      ],
      [
        chatUsersLabelsList,
        localization.options.assignee.label,
        localization.options.lastStatus.label,
        localization.options.lastStatus.value.close,
        localization.options.lastStatus.value.open,
        localization.options.lastStatusTimeAndDate.label,
        selectableAssignees,
      ],
    );

    const { viewId } = useParams();
    const navigate = useNavigate();

    const [filtersId, setFiltersId] = React.useState<string | number | null>(null);
    const [filtersName, setFiltersName] = React.useState<string>('');
    const [filtersConditions, setFiltersConditions] = React.useState<ViewCondition[]>([]);

    React.useImperativeHandle(ref, () => ({
      setData,
      clearData,
    }));

    const setData = React.useCallback(
      (id: string | number | null, name: string, conditions: any[]) => {
        setFiltersId(id);
        setFiltersName(name);
        setFiltersConditions(conditions);
      },
      [setFiltersId, setFiltersName, setFiltersConditions],
    );
    const clearData = React.useCallback(() => {
      setFiltersId(null);
      setFiltersName('');
      setFiltersConditions([]);
    }, []);
    const handleClose = () => {
      onClose();
      setTimeout(() => {
        clearData();
      }, 300);
    };

    const handleFiltersName = (value: string) => {
      setFiltersName(value);
    };
    //* Conditions handlers
    const addNewCondition = () => {
      const newConditionObj = {
        id: generateUniqueId(
          filtersConditions.map((cond) => cond.id.toString()),
          5,
        ),
        key: '' as keyof ChatUserModel,
        value: null,
      };

      setFiltersConditions((prevList) => [...prevList, newConditionObj]);
    };
    const deleteCondition = (id: string | number) => {
      setFiltersConditions((prevList) => prevList.filter((cond) => cond.id !== id));
    };
    const handleConditionParams = (id: string | number, params: Partial<ViewCondition>) => {
      setFiltersConditions((prevList) => {
        const newList = [...prevList];
        const targetConditionIndex = newList.findIndex((cond) => cond.id === id);
        newList[targetConditionIndex] = {
          ...newList[targetConditionIndex],
          ...params,
        };
        return newList;
      });
    };

    const saveCurrentFilters = (id: string | number | null = null) => {
      handleClose();
      const storeState = store.getState();

      let newFiltersAll = [...storeState.userConfig.customViews];
      if (createOrEdit === 'edit') {
        const currentFilters = newFiltersAll.find((filters) => filters.id === id);
        if (currentFilters) {
          const newFilters = {
            ...currentFilters,
            name: filtersName,
            filters: filtersConditions,
          };

          newFiltersAll = newFiltersAll.map((filter) => {
            if (filter.id === currentFilters.id) {
              return newFilters;
            }
            return filter;
          });

          if (viewId === currentFilters.id.toString())
            navigate(`/chats/${storeState.chat.selectedChatUser?.UserDisplayPhoneNumber || ''}`);
        }
      } else {
        const newFilterObj = {
          name: filtersName,
          filters: filtersConditions,
          id: generateUniqueId(
            newFiltersAll.map((filter) => filter.id.toString()),
            5,
          ),
        };

        newFiltersAll.push(newFilterObj);
      }

      fetchUpdateUserConfig(
        {
          customViews: newFiltersAll,
        },
        true,
      );
    };

    const buttonDisabled = React.useMemo(() => {
      return filtersName === '' || filtersConditions.length === 0;
    }, [filtersConditions.length, filtersName]);

    return (
      <Dialog onClose={handleClose} open={open} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Grid container spacing={2} alignItems={'center'}>
            <Grid size="grow">{localization.title}</Grid>
            <Grid size="auto">
              <IconButton onClick={handleClose}>
                <CloseOutlinedIcon />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>

        <DialogContent
          sx={{
            pt: '8px !important',
          }}
        >
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                fullWidth
                label={localization.nameLabel}
                value={filtersName}
                onChange={(e) => handleFiltersName(e.target.value)}
              />
            </Grid>
            <Grid size={12}>
              <Divider />
            </Grid>
            <Grid size={12}>
              <CreateCustomViewConditions
                options={filterOptions}
                conditions={filtersConditions}
                addNewCondition={addNewCondition}
                deleteCondition={deleteCondition}
                onChange={handleConditionParams}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" onClick={handleClose}>
            {localization.close}
          </Button>
          <Button variant="contained" onClick={() => saveCurrentFilters(filtersId)} disabled={buttonDisabled}>
            {localization.save}
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

export default CreateCustomView;
